import React, { useEffect } from 'react';

// 공통 커스텀 모달 (alert/confirm 대체)
// type: 'confirm' | 'danger' | 'success'
function CustomModal(props) {
  var isOpen = props.isOpen;
  var type = props.type || 'confirm';
  var title = props.title || '';
  var message = props.message || '';
  var onConfirm = props.onConfirm;
  var onCancel = props.onCancel;
  // fix/custom-modal-danger-confirm (2026-05-24) — hideCancel 먼저 계산.
  //   기존: type==='danger' 면 무조건 '삭제' → hideCancel:true 알림용 ('실패' 등) 도 '삭제' 표시 → UX 혼동
  //   fix: hideCancel=true 면 '확인' 강제 (type 무관). 명시 confirmText prop 은 그대로 우선.
  var hideCancel = type === 'success' || props.hideCancel;
  var defaultConfirmText = hideCancel ? '확인' : (type === 'danger' ? '삭제' : '확인');
  var confirmText = props.confirmText || defaultConfirmText;
  var cancelText = props.cancelText || '취소';

  useEffect(function() {
    if (!isOpen) return;
    var onKey = function(e) {
      if (e.key === 'Escape' && onCancel) onCancel();
      if (e.key === 'Enter' && onConfirm) onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return function() { window.removeEventListener('keydown', onKey); };
  }, [isOpen, onConfirm, onCancel]);

  if (!isOpen) return null;

  var iconBg = { confirm: '#E6F1FB', danger: '#FCEBEB', success: '#E1F5EE' }[type];
  var iconColor = { confirm: '#1B3A5C', danger: '#E24B4A', success: '#1D9E75' }[type];
  var confirmBg = { confirm: '#1B3A5C', danger: '#E24B4A', success: '#1D9E75' }[type];

  var iconSvg = null;
  if (type === 'confirm') {
    iconSvg = (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8" x2="12" y2="13"/>
        <line x1="12" y1="16.5" x2="12" y2="16.5"/>
      </svg>
    );
  } else if (type === 'danger') {
    iconSvg = (
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13.5"/>
        <line x1="12" y1="17" x2="12" y2="17"/>
      </svg>
    );
  } else {
    iconSvg = (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={iconColor} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
      </svg>
    );
  }

  var overlayStyle = {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0,0,0,0.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
    fontFamily: 'Pretendard, -apple-system, BlinkMacSystemFont, sans-serif',
    animation: 'cmFade 0.2s ease'
  };
  var cardStyle = {
    background: '#fff',
    borderRadius: 16,
    padding: '28px 24px 20px',
    maxWidth: 380,
    width: '90%',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    animation: 'cmPop 0.3s ease',
    textAlign: 'center'
  };
  var iconWrapStyle = {
    width: 48, height: 48, borderRadius: '50%',
    background: iconBg,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 14px'
  };
  var titleStyle = { fontSize: 17, fontWeight: 500, color: '#1e293b', marginBottom: 8, lineHeight: 1.4 };
  var msgStyle = { fontSize: 14, color: '#64748b', lineHeight: 1.5, marginBottom: 22, whiteSpace: 'pre-line' };
  var btnRowStyle = { display: 'flex', gap: 8, justifyContent: 'center' };
  var btnBase = {
    border: 'none', borderRadius: 10, padding: '10px 24px',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: 'inherit', minWidth: 90,
    transition: 'transform 0.1s ease, filter 0.15s ease'
  };
  var cancelBtnStyle = Object.assign({}, btnBase, { background: '#f1f5f9', color: '#475569' });
  var confirmBtnStyle = Object.assign({}, btnBase, { background: confirmBg, color: '#fff' });

  var hoverIn = function(e) { e.currentTarget.style.filter = 'brightness(1.08)'; };
  var hoverOut = function(e) { e.currentTarget.style.filter = 'brightness(1)'; };
  var pressDn = function(e) { e.currentTarget.style.transform = 'scale(0.97)'; };
  var pressUp = function(e) { e.currentTarget.style.transform = 'scale(1)'; };

  var onOverlayClick = function(e) {
    if (e.target === e.currentTarget && onCancel) onCancel();
  };

  return (
    <div style={overlayStyle} onClick={onOverlayClick}>
      <style>{
        '@keyframes cmFade { from { opacity: 0; } to { opacity: 1; } }' +
        '@keyframes cmPop { from { transform: scale(0.92) translateY(8px); opacity: 0; } to { transform: scale(1) translateY(0); opacity: 1; } }'
      }</style>
      <div style={cardStyle} onClick={function(e) { e.stopPropagation(); }}>
        <div style={iconWrapStyle}>{iconSvg}</div>
        {title && <div style={titleStyle}>{title}</div>}
        {message && <div style={msgStyle}>{message}</div>}
        <div style={btnRowStyle}>
          {!hideCancel && (
            <button style={cancelBtnStyle} onClick={onCancel}
              onMouseEnter={hoverIn} onMouseLeave={hoverOut} onMouseDown={pressDn} onMouseUp={pressUp}>
              {cancelText}
            </button>
          )}
          <button style={confirmBtnStyle} onClick={onConfirm}
            onMouseEnter={hoverIn} onMouseLeave={hoverOut} onMouseDown={pressDn} onMouseUp={pressUp}>
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CustomModal;
